// Phase 15.8 — FASTag Route Map (LorryInfo)
// Free Leaflet + OpenStreetMap (CARTO) — no API key required.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  MapContainer, TileLayer, Marker, Polyline, Popup, useMap,
} from "react-leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MapPin, RefreshCw, Search, Truck, AlertTriangle, CheckCircle2, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Numbered Leaflet marker icon — uses divIcon (no external image needed)
const makeNumberedIcon = (n, latest) =>
  L.divIcon({
    className: "",
    html: `<div style="
      background:${latest ? "#dc2626" : "#0ea5e9"};
      color:white;
      width:32px;height:32px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:13px;font-weight:700;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      border:3px solid white;
    ">${n}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const directionLabel = {
  N: "North", S: "South", E: "East", W: "West",
};

export default function FastagMap() {
  const [trucks, setTrucks] = useState([]);
  const [selectedNo, setSelectedNo] = useState("");
  const [manual, setManual] = useState("");
  const [crossings, setCrossings] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/api/fastag/status`),
          axios.get(`${API}/api/fastag/trucks`),
        ]);
        setLive(!!s.data.live);
        setTrucks(t.data.trucks || []);
      } catch (e) {
        toast.error("Failed to load trucks list");
      }
    })();
  }, []);

  const fetchCrossings = async (vehicleNo) => {
    if (!vehicleNo || vehicleNo.length < 4) {
      toast.error("Enter a valid vehicle number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/fastag/crossings`,
        { params: { vehicle_no: vehicleNo } },
      );
      setCrossings(data.crossings || []);
      setMeta(data);
      if (data.provider_error) {
        toast.error(`LorryInfo: ${data.provider_error}`, { duration: 8000 });
      } else if (!data.live) {
        toast.warning("LORRYINFO_API_KEY missing — showing mock empty data. Add key to backend/.env and restart.");
      } else if ((data.crossings || []).length === 0) {
        toast.info("No crossings returned (vehicle may have no recent toll activity)");
      } else {
        const persisted = data.persisted || {};
        if (persisted.skipped) {
          toast.warning(`${data.crossings.length} plazas plotted, but NOT logged: ${persisted.reason}`, { duration: 7000 });
        } else if (persisted.inserted > 0) {
          toast.success(`${data.crossings.length} plazas plotted · ${persisted.inserted} new logged, ${persisted.duplicates} duplicate`);
        } else if (persisted.duplicates > 0) {
          toast.success(`${data.crossings.length} plazas plotted · ${persisted.duplicates} already in log (no new)`);
        } else {
          toast.success(`${data.crossings.length} plazas plotted`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    }
    setLoading(false);
  };

  const points = useMemo(
    () => crossings.filter((c) => c.lat && c.lon).map((c) => [c.lat, c.lon]),
    [crossings],
  );

  const onSearch = () => {
    const no = (manual || selectedNo || "").trim().toUpperCase();
    if (!no) return toast.error("Pick a truck or type a vehicle number");
    fetchCrossings(no);
  };

  return (
    <div className="space-y-4" data-testid="fastag-map-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-emerald-600" />
            FASTag Route Map
          </h1>
          <p className="text-sm text-muted-foreground">
            Toll plaza crossings on map (powered by LorryInfo · OpenStreetMap)
          </p>
        </div>
        <div className="ml-auto">
          <Badge
            className={live ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}
            data-testid="fastag-live-badge"
          >
            {live ? (
              <><CheckCircle2 className="w-3 h-3 mr-1 inline" /> LIVE</>
            ) : (
              <><AlertTriangle className="w-3 h-3 mr-1 inline" /> MOCK MODE — key missing</>
            )}
          </Badge>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground">Pick truck</label>
              <Select value={selectedNo} onValueChange={(v) => { setSelectedNo(v); setManual(""); }}>
                <SelectTrigger className="w-[220px]" data-testid="truck-select">
                  <SelectValue placeholder="Select a truck..." />
                </SelectTrigger>
                <SelectContent>
                  {trucks.map((t) => (
                    <SelectItem key={t.id} value={t.truck_no}>
                      <span className="font-mono">{t.truck_no}</span>
                      {t.fastag_tag_id ? <span className="ml-1 text-[10px] text-green-600">●</span> : null}
                    </SelectItem>
                  ))}
                  {trucks.length === 0 && <SelectItem disabled value="_none">No trucks</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground">…or vehicle no</label>
              <Input
                placeholder="RJ14GP7920"
                value={manual}
                onChange={(e) => { setManual(e.target.value.toUpperCase()); setSelectedNo(""); }}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                data-testid="manual-vehicle-input"
              />
            </div>
            <Button
              onClick={onSearch}
              disabled={loading || (!manual && !selectedNo)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="fetch-crossings-btn"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Fetch Crossings
            </Button>
          </div>
        </CardContent>
      </Card>

      {meta && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Plazas" value={meta.total_plazas || 0} icon={MapPin} />
          <StatCard label="Vehicle" value={meta.vehicle_no} icon={Truck} mono />
          <StatCard label="First Crossing" value={fmt(meta.first_at)} icon={Clock} small />
          <StatCard label="Duration" value={meta.duration_hrs != null ? `${meta.duration_hrs} h` : "—"} icon={Clock} />
          <StatCard label="Total Toll" value={meta.total_amount != null ? `₹${Math.round(meta.total_amount)}` : "—"} icon={MapPin} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
        {/* MAP */}
        <Card className="overflow-hidden" data-testid="fastag-map-container">
          <div style={{ height: 520, width: "100%" }}>
            <MapContainer
              center={[26.0, 75.0]}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              />
              {points.length > 0 && <FitBounds points={points} />}
              {points.length > 1 && (
                <Polyline
                  positions={points}
                  pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 0.7 }}
                />
              )}
              {crossings.filter((c) => c.lat && c.lon).map((c, i, arr) => {
                const isLatest = i === arr.length - 1;
                return (
                  <Marker
                    key={c.seq_no}
                    position={[c.lat, c.lon]}
                    icon={makeNumberedIcon(i + 1, isLatest)}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{c.plaza}</div>
                        {c.plaza_display_name && (
                          <div className="text-slate-500 italic">{c.plaza_display_name}</div>
                        )}
                        <div>{fmt(c.datetime_iso)}</div>
                        {c.toll_amount != null && (
                          <div className="font-semibold text-emerald-700">
                            ₹{c.toll_amount} · {c.toll_rate_type || ""}
                          </div>
                        )}
                        {c.lane_direction && (
                          <div>Direction: {directionLabel[c.lane_direction] || c.lane_direction}</div>
                        )}
                        {c.vehicle_class_description && (
                          <div>{c.vehicle_class_description}</div>
                        )}
                        <div className="text-slate-500 font-mono text-[10px] mt-1">{c.seq_no}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </Card>

        {/* TIMELINE SIDEBAR */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Plaza Timeline</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[480px] overflow-y-auto">
            {crossings.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">
                Pick a truck above and click <b>Fetch Crossings</b>.
              </div>
            ) : (
              <ol className="divide-y" data-testid="plaza-timeline">
                {[...crossings].reverse().map((c, idx, arr) => {
                  const num = crossings.length - idx;
                  const isLatest = idx === 0;
                  return (
                    <li key={c.seq_no} className="p-3 flex items-start gap-2 hover:bg-slate-50">
                      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${isLatest ? "bg-red-600" : "bg-sky-500"}`}>
                        {num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" title={c.plaza}>{c.plaza || "Unknown plaza"}</div>
                        <div className="text-xs text-muted-foreground">{fmt(c.datetime_iso)}</div>
                        <div className="text-[10px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                          {c.toll_amount != null && (
                            <span className="text-emerald-700 font-semibold">₹{c.toll_amount}</span>
                          )}
                          {c.toll_rate_type && <span>· {c.toll_rate_type}</span>}
                          {c.lane_direction && <span>· {directionLabel[c.lane_direction]}</span>}
                          {c.vehicle_class_description && <span>· {c.vehicle_class_description}</span>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-[10px] text-slate-500 px-1">
        Tip: Marker click karke plaza ka name, timestamp, direction aur sequence-no dekh sakte hain. Map tiles powered by CARTO ·  OpenStreetMap (free, no key).
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, small, mono }) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-emerald-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className={`font-semibold truncate ${small ? "text-sm" : "text-base"} ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}
